import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { authService } from '../services/api';
import { User, LoginRequest, RegisterRequest } from '../types/api';
import { logger } from '../lib/logger';

// Global flag to prevent re-initialization during HMR
let authInitialized = false;

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize from localStorage immediately to survive HMR
  const [user, setUser] = useState<User | null>(() => authService.getUser());
  const [isLoading, setIsLoading] = useState(!authInitialized);
  const initRef = useRef(false);

  // При загрузке приложения проверяем сохраненную сессию
  useEffect(() => {
    // Skip if already initialized (HMR protection)
    if (authInitialized || initRef.current) {
      logger.log('🔄 Auth: Skipping re-init (HMR detected)');
      setIsLoading(false);
      return;
    }
    initRef.current = true;

    const initAuth = async () => {
      const savedUser = authService.getUser();
      const token = authService.getToken();

      if (savedUser && token) {
        logger.log('🔄 Auth: Restoring session from localStorage');
        // Сразу устанавливаем сохранённого пользователя
        setUser(savedUser);

        // Retry function for backend restart scenarios
        const verifyToken = async (retries = 3, delay = 500): Promise<void> => {
          try {
            // Проверяем, валиден ли токен в фоне
            const { user: currentUser } = await authService.getCurrentUser();
            setUser(currentUser);
            logger.log('✅ Session restored successfully');
          } catch (error: any) {
            // Logout только при 401, при других ошибках пробуем повторить
            if (error?.response?.status === 401) {
              logger.log('❌ Token invalid (401), clearing session');
              authService.logoutSync();
              setUser(null);
            } else if (retries > 0) {
              // Network error - retry after delay (for backend restart scenarios)
              logger.warn(`⚠️ API error, retrying in ${delay}ms... (${retries} retries left)`);
              await new Promise(r => setTimeout(r, delay));
              return verifyToken(retries - 1, delay * 2);
            } else {
              logger.warn('⚠️ API error, keeping cached user:', error?.message);
              // Оставляем savedUser - уже установлен выше
            }
          }
        };

        await verifyToken();
      } else {
        logger.log('🔄 Auth: No saved session, login required');
      }
      authInitialized = true;
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (data: LoginRequest) => {
    logger.log('🔐 AuthContext.login called');
    setIsLoading(true);
    try {
      logger.log('🔐 Calling authService.login...');
      const response = await authService.login(data);
      logger.log('✅ authService.login response:', response);
      setUser(response.user);
      logger.log('✅ User set in context:', response.user);
    } catch (error) {
      logger.error('❌ AuthContext.login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterRequest) => {
    // НЕ устанавливаем isLoading на уровне контекста!
    // Это вызовет размонтирование компонента Register через PublicRoute
    // Регистрируем пользователя, но НЕ логиним его автоматически
    await authService.register(data);
    // Пользователь должен войти вручную после регистрации
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  // Listen for auth:logout event from apiClient (auto-logout on token refresh failure)
  useEffect(() => {
    const handleAutoLogout = () => {
      logger.log('🔐 Auto-logout triggered (token refresh failed)');
      authService.logoutSync();
      setUser(null);
    };

    window.addEventListener('auth:logout', handleAutoLogout);
    return () => window.removeEventListener('auth:logout', handleAutoLogout);
  }, []);

  const refreshUser = async (retries = 2, delay = 500): Promise<void> => {
    try {
      const { user: currentUser } = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error: any) {
      // Logout только при 401 (unauthorized), не при сетевых ошибках!
      if (error?.response?.status === 401) {
        logger.log('❌ Token expired (401), logging out');
        logout();
      } else if (retries > 0) {
        // Network error - retry after delay
        logger.warn(`⚠️ Failed to refresh user, retrying in ${delay}ms...`);
        await new Promise(r => setTimeout(r, delay));
        return refreshUser(retries - 1, delay * 2);
      } else {
        logger.warn('⚠️ Failed to refresh user, keeping session:', error?.message);
      }
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// No default export - use named exports only for Fast Refresh compatibility
